import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';

import {
  CompanyCampusDocument,
  CompanyCampusQuery,
  CompanyCampusQueryVariables,
  CompanyHeroDocument,
  CompanyHeroQuery,
  CompanyHeroQueryVariables,
  CompanyLargeImageDocument,
  CompanyLargeImageQuery,
  CompanyLargeImageQueryVariables,
  CompanyMapSectionDocument,
  CompanyMapSectionQuery,
  CompanyMapSectionQueryVariables,
  CompanyNewsroomDocument,
  CompanyNewsroomQuery,
  CompanyNewsroomQueryVariables,
  CompanySeoDocument,
  CompanySeoQuery,
  CompanySeoQueryVariables,
  CompanySliderImageSectionDocument,
  CompanySliderImageSectionQuery,
  CompanySliderImageSectionQueryVariables,
  CompanyTeamDocument,
  CompanyTeamQuery,
  CompanyTeamQueryVariables,
  CompanyValuesDocument,
  CompanyValuesQuery,
  CompanyValuesQueryVariables,
  FooterDocument,
  FooterQuery,
  FooterQueryVariables,
  FormDocument,
  FormQuery,
  FormQueryVariables,
  NavigationDocument,
  NavigationQuery,
  NavigationQueryVariables,
} from '@/api';
import { addApolloState, initializeApollo } from '@/api/apolloClient';
import { CompanyPage } from '@/views/company';

export const getStaticProps: GetStaticProps = async () => {
  const client = initializeApollo();

  // prefetch
  await client.query({ query: CompanyHeroDocument });
  await client.query({ query: CompanySliderImageSectionDocument });
  await client.query({ query: CompanyValuesDocument });
  await client.query({ query: CompanyCampusDocument });
  await client.query({ query: CompanyMapSectionDocument });
  await client.query({ query: CompanyTeamDocument });
  await client.query({ query: CompanyLargeImageDocument });
  await client.query({ query: CompanyNewsroomDocument });
  await client.query({ query: NavigationDocument });
  await client.query({ query: FooterDocument });
  await client.query({ query: FormDocument });

  const { data: heroData } = await client.query<
    CompanyHeroQuery,
    CompanyHeroQueryVariables
  >({
    query: CompanyHeroDocument,
  });

  const { data: sliderImageSectionData } = await client.query<
    CompanySliderImageSectionQuery,
    CompanySliderImageSectionQueryVariables
  >({
    query: CompanySliderImageSectionDocument,
  });

  const { data: valuesData } = await client.query<
    CompanyValuesQuery,
    CompanyValuesQueryVariables
  >({ query: CompanyValuesDocument });

  const { data: campusData } = await client.query<
    CompanyCampusQuery,
    CompanyCampusQueryVariables
  >({
    query: CompanyCampusDocument,
  });

  const { data: mapSectionData } = await client.query<
    CompanyMapSectionQuery,
    CompanyMapSectionQueryVariables
  >({
    query: CompanyMapSectionDocument,
  });

  const { data: teamCardsSectionData } = await client.query<
    CompanyTeamQuery,
    CompanyTeamQueryVariables
  >({
    query: CompanyTeamDocument,
  });

  const { data: largeImageSectionData } = await client.query<
    CompanyLargeImageQuery,
    CompanyLargeImageQueryVariables
  >({
    query: CompanyLargeImageDocument,
  });

  const { data: newsroomData } = await client.query<
    CompanyNewsroomQuery,
    CompanyNewsroomQueryVariables
  >({
    query: CompanyNewsroomDocument,
  });

  const { data: seoData } = await client.query<
    CompanySeoQuery,
    CompanySeoQueryVariables
  >({ query: CompanySeoDocument });

  const { data: formData } = await client.query<FormQuery, FormQueryVariables>({
    query: FormDocument,
  });

  const { data: footerData } = await client.query<
    FooterQuery,
    FooterQueryVariables
  >({ query: FooterDocument });

  const { data: headerData } = await client.query<
    NavigationQuery,
    NavigationQueryVariables
  >({ query: NavigationDocument });

  return addApolloState(client, {
    props: {
      seoData,
      heroData,
      sliderImageSectionData,
      valuesData,
      campusData,
      mapSectionData,
      teamCardsSectionData,
      largeImageSectionData,
      newsroomData,
      formData,
      footerData,
      headerData,
    },
  });
};

const Company: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  seoData,
  heroData,
  sliderImageSectionData,
  valuesData,
  campusData,
  mapSectionData,
  teamCardsSectionData,
  largeImageSectionData,
  newsroomData,
  formData,
  footerData,
  headerData,
}) => (
  <CompanyPage
    seo={seoData?.company?.data?.attributes?.seo}
    hero={heroData?.company?.data?.attributes?.heroVideoSection}
    sliderImageSection={
      sliderImageSectionData?.company?.data?.attributes?.sliderImageSection
    }
    values={valuesData?.company?.data?.attributes?.values}
    campus={campusData}
    mapSection={mapSectionData?.company?.data?.attributes?.mapSection}
    teamCardsSection={
      teamCardsSectionData?.company?.data?.attributes?.teamCards
    }
    largeImageSection={
      largeImageSectionData?.company?.data?.attributes?.largeImageSection
    }
    newsroom={newsroomData?.company?.data?.attributes?.newsroom}
    formData={formData}
    footerData={footerData}
    headerData={headerData}
  />
);

export default Company;
